import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Checkbox,
  Badge,
} from '../ui'
import type { Commission, CommissionRule } from '../../types'
import {
  evaluateCommissionRuleForSale,
  type CommissionRuleSaleEvalContext,
} from '../../utils/commissionRuleSaleEvaluation'

export type CommissionActiveRulesTableProps = {
  rules: CommissionRule[]
  sale: { commissions?: Commission[] | null } | null | undefined
  evalContext: CommissionRuleSaleEvalContext | null
}

export const CommissionActiveRulesTable: React.FC<CommissionActiveRulesTableProps> = ({
  rules,
  sale,
  evalContext,
}) => {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('financialSales.commissionRuleEvaluation.sectionTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">{t('financialSales.empty')}</p>
        ) : evalContext ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 text-center">
                  {t('financialSales.commissionRuleEvaluation.onSaleColumn')}
                </TableHead>
                <TableHead>{t('financialSales.commissionRuleEvaluation.ruleColumn')}</TableHead>
                <TableHead>{t('financialSales.commissionRuleEvaluation.applicableToColumn')}</TableHead>
                <TableHead>{t('financialSales.commissionRuleEvaluation.statusColumn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => {
                const ev = evaluateCommissionRuleForSale(rule, evalContext)
                const hasLine = Boolean(sale?.commissions?.some((c) => c.ruleId === rule.id))
                const at = rule.applicableTo || 'salesperson'
                const appTo = t(`financialSales.commissionRuleEvaluation.applicableTo.${at}` as never)
                return (
                  <TableRow key={rule.id}>
                    <TableCell className="text-center align-top">
                      <Checkbox checked={hasLine} disabled className="opacity-100" />
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{rule.name}</span>
                        {rule.isMandatory && (
                          <Badge variant="warning" className="text-xs">
                            {t('financialSales.commissions.mandatoryBadge')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground align-top">{appTo}</TableCell>
                    <TableCell className="text-sm align-top">
                      {!ev.applicable ? (
                        <div>
                          <p className="font-medium text-destructive">
                            {t('financialSales.commissionRuleEvaluation.notApplicable')}
                          </p>
                          <ul className="list-disc list-inside mt-1 text-muted-foreground space-y-0.5">
                            {ev.reasonKeys.map((k) => (
                              <li key={k}>
                                {t(`financialSales.commissionRuleEvaluation.reasons.${k}` as never)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {hasLine && (
                            <p className="text-foreground">
                              {t('financialSales.commissionRuleEvaluation.enabledOnSale')}
                            </p>
                          )}
                          {!hasLine && rule.isMandatory && (
                            <p className="text-muted-foreground">
                              {t('financialSales.commissionRuleEvaluation.mandatoryWillApplyHint')}
                            </p>
                          )}
                          {!hasLine && !rule.isMandatory && (
                            <p className="text-muted-foreground">
                              {t('financialSales.commissionRuleEvaluation.availableToAddHint')}
                            </p>
                          )}
                          {ev.infoKeys.map((k) => (
                            <p key={k} className="text-xs text-muted-foreground">
                              {t(`financialSales.commissionRuleEvaluation.info.${k}` as never)}
                            </p>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground py-2">—</p>
        )}
      </CardContent>
    </Card>
  )
}
